import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, Paper, TextField, Button, Box, Grid, Alert
} from '@mui/material';
import api from '../utils/api';

const CustomerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    // If in edit mode, fetch the customer data
    if (isEditMode) {
      fetchCustomerData();
    }
  }, [isEditMode, id]);
  
  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/customers/${id}`);
      setFormData(response.data);
    } catch (err) {
      console.error('Error fetching customer data:', err);
      setError('Virhe asiakkaan tietojen haussa. Yritä myöhemmin uudelleen.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      if (isEditMode) {
        await api.put(`/api/customers/${id}`, formData);
        setSuccess('Asiakas päivitetty onnistuneesti!');
      } else {
        await api.post('/api/customers', formData);
        setSuccess('Uusi asiakas luotu onnistuneesti!');
        // Reset form after successful creation
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: ''
        });
      }
      
      // Navigate back to list after a short delay
      setTimeout(() => {
        navigate('/customers');
      }, 2000);
      
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Virhe lomakkeen lähetyksessä. Tarkista tiedot ja yritä uudelleen.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/customers');
  };

  return (
    <div>
      <Typography variant="h4" component="h1" gutterBottom>
        {isEditMode ? 'Muokkaa asiakasta' : 'Uusi asiakas'}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
      )}
      
      <Paper elevation={3} sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Nimi"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sähköposti"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Puhelin"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Osoite"
                name="address"
                multiline
                rows={3}
                value={formData.address}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Peruuta
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  {loading ? 'Tallennetaan...' : (isEditMode ? 'Päivitä' : 'Tallenna')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </div>
  );
};

export default CustomerForm;
